import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { ReactComponent as Lock } from "../assets/lock.svg";
import config from "../config";

const QandA = ({ selectedDate, onBackButtonClick, backButtonPressed }) => {
  const initData = window.Telegram.WebApp.initData;
  const [answers, setAnswers] = useState({});
  const [, setOriginalData] = useState({ answers: {} });
  const [isModified, setIsModified] = useState(false);
  const textareasRef = useRef({});
  const [activeType, setActiveType] = useState('daily');

  const isLastDayOfMonth = useCallback((date) => {
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getMonth() !== date.getMonth();
  }, []);

  const isSunday = useCallback((date) => {
    return date.getDay() === 0;
  }, []);

  const getAvailableTypes = useCallback((date) => {
    const types = ['daily'];

    if (isSunday(date)) {
      types.push('weekly');
    }

    if (isLastDayOfMonth(date)) {
      types.push('monthly');
    }

    return types;
  }, [isSunday, isLastDayOfMonth]);

  useEffect(() => {
    const availableTypes = getAvailableTypes(selectedDate);
    if (availableTypes.includes('monthly')) {
      setActiveType('monthly');
    } else if (availableTypes.includes('weekly')) {
      setActiveType('weekly');
    } else {
      setActiveType('daily');
    }
  }, [selectedDate, getAvailableTypes]);

  const getInitialAnswers = useCallback((type) => {
    switch (type) {
      case 'weekly':
        return { 1: "", 2: "", 3: "", 4: "", 5: "", 6: "", 7: "", 8: "", 9: "", 10: "" };
      case 'monthly':
        return { 1: "", 2: "", 3: "", 4: "", 5: "", 6: "", 7: "", 8: "", 9: "", 10: "" };
      default:
        return { 1: "", 2: "", 3: "", 4: "", 5: "", 6: "", 7: "", 8: "", 9: "" };
    }
  }, []);

  const loadAnswers = useCallback((type) => {
    const initialAnswers = getInitialAnswers(type);

    axios
      .post(`${config.API_BASE_URL}/qanda/get`, {
        selectedDate,
        initData,
        type
      })
      .then((response) => {
        if (response.data && Object.keys(response.data.data).length !== 0) {
          setAnswers(response.data.data);
          setOriginalData({ answers: response.data.data });
        } else {
          setAnswers(initialAnswers);
          setOriginalData({ answers: {} });
        }
      })
      .catch((error) => console.error("Ошибка загрузки данных:", error));
  }, [selectedDate, initData, getInitialAnswers]);

  useEffect(() => {
    loadAnswers(activeType);
  }, [selectedDate, initData, activeType, loadAnswers]);

  useEffect(() => {
    Object.values(textareasRef.current).forEach(adjustHeight);
  }, [answers]);

  const handleSave = useCallback(() => {
    axios
      .post(`${config.API_BASE_URL}/qanda/save`, {
        selectedDate,
        initData,
        answers,
        type: activeType
      })
      .then(() => {
        setOriginalData({ answers });
        setIsModified(false);
      })
      .catch((error) => console.error("Ошибка сохранения данных:", error));
  }, [selectedDate, initData, answers, activeType]);

  useEffect(() => {
    if (backButtonPressed) {
      onBackButtonClick(false);
      if (isModified) {
        handleSave();
      }
    }
  }, [backButtonPressed, handleSave, onBackButtonClick, isModified]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnswers((prevState) => {
      const newState = { ...prevState, [name]: value };
      setIsModified(true);
      return newState;
    });

    adjustHeight(e.target);
  };

  const adjustHeight = (element) => {
    if (element) {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  const handleTypeChange = (type) => {
    if (getAvailableTypes(selectedDate).includes(type)) {
      setActiveType(type);
    }
  };

  const renderQuestions = () => {
    const renderInput = (number, placeholder) => (
      <div className="input-group">
        <h4 className="input-label">{placeholder}</h4>
        <textarea
          ref={(el) => (textareasRef.current[number] = el)}
          className="auto-expand"
          name={number.toString()}
          value={answers[number] || ""}
          maxLength={350}
          onChange={handleChange}
        />
      </div>
    );

    switch (activeType) {
      case 'weekly':
        return (
          <>
            {renderInput(1, "Какие цели я ставил на эту неделю, и что из этого удалось реализовать?")}
            {renderInput(2, "Что было самым значимым событием недели?")}
            {renderInput(3, "Какие трудности я встретил, и как я с ними справился?")}
            {renderInput(4, "Как я распределял свое время? Было ли оно потрачено эффективно?")}
            {renderInput(5, "Что доставило мне радость и удовлетворение на этой неделе?")}
            {renderInput(6, "Были ли моменты, когда я действовал не так, как хотел бы? Что можно исправить?")}
            {renderInput(7, "Как я заботился о своем здоровье, энергии и эмоциональном состоянии?")}
            {renderInput(8, "Что я узнал нового или какие навыки улучшил?")}
            {renderInput(9, "Как мои действия в этой неделе приближают меня к моим долгосрочным целям?")}
            {renderInput(10, "Что я могу улучшить в следующей неделе, чтобы она стала еще продуктивнее и осознаннее?")}
          </>
        );
      case 'monthly':
        return (
          <>
            {renderInput(1, "Каким был мой главный фокус в этом месяце? Достиг ли я того, к чему стремился?")}
            {renderInput(2, "Какие значимые события произошли, и как они на меня повлияли?")}
            {renderInput(3, "Что мне удалось сделать лучше, чем в прошлом месяце?")}
            {renderInput(4, "Какие моменты или решения я бы хотел изменить, если была бы такая возможность?")}
            {renderInput(5, "Как я справлялся с трудностями и стрессовыми ситуациями?")}
            {renderInput(6, "Какие новые знания, навыки или инсайты я получил?")}
            {renderInput(7, "Как я заботился о своем физическом и ментальном здоровье?")}
            {renderInput(8, "Что доставило мне наибольшую радость и удовлетворение?")}
            {renderInput(9, "Какие привычки или действия мешали мне двигаться вперед?")}
            {renderInput(10, "Что я хочу изменить или улучшить в следующем месяце, чтобы он был более продуктивным и осознанным?")}
          </>
        );
      default:
        return (
          <>
            {renderInput(1, "Как прошел мой день?")}
            {renderInput(2, "Что я сделал для достижения своих целей?")}
            {renderInput(3, "Что нового я узнал сегодня?")}
            {renderInput(4, "Что я ценю из того, что за сегодня произошло?")}
            {renderInput(5, "О чем я сожалею?")}
            {renderInput(6, "Почему я так себя чувствую?")}
            {renderInput(7, "Какие ценные уроки я могу извлечь из всего этого?")}
            {renderInput(8, "Что я могу сделать завтра, чтобы стать еще лучше?")}
            {renderInput(9, "От чего я должен избавиться или убрать, чтобы улучшить себя с завтрашнего дня?")}
          </>
        );
    }
  };

  const availableTypes = getAvailableTypes(selectedDate);

  return (
    <div className="task-list">
      <div className="now-date">{selectedDate.toLocaleDateString('ru-RU')}</div>
      <div className="section victories">
        <div className="titles">
          <h3 className="section-title">Саморефлексия</h3>
        </div>
        <div className="reflection-tabs">
          <button
            className={`reflection-tab ${activeType === 'daily' ? 'active' : ''}`}
            onClick={() => handleTypeChange('daily')}
          >
            За день
          </button>
          <button
            className={`reflection-tab ${activeType === 'weekly' ? 'active' : ''} ${!availableTypes.includes('weekly') ? 'disabled' : ''}`}
            onClick={() => handleTypeChange('weekly')}
            disabled={!availableTypes.includes('weekly')}
          >
            {!availableTypes.includes('weekly') && <Lock className="lock-icon" />}
            За неделю
          </button>
          <button
            className={`reflection-tab ${activeType === 'monthly' ? 'active' : ''} ${!availableTypes.includes('monthly') ? 'disabled' : ''}`}
            onClick={() => handleTypeChange('monthly')}
            disabled={!availableTypes.includes('monthly')}
          >
            {!availableTypes.includes('monthly') && <Lock className="lock-icon" />}
            За месяц
          </button>
        </div>
        {renderQuestions()}
      </div>
    </div>
  );
};

export default QandA;
